import React from 'react'
import { Box, Heading, Text, Caption, Paragraph } from '@realsoftworks/decor'
import styled from 'styled-components'
import Card from 'common/Card'
import { monetary } from 'common/util/number'
import { Numeric, Monetary } from 'common/format'
import LocationTooltip from '../../LocationTooltip'
import * as dateUtil from 'common/util/date'
import AddressPreviewLink from 'propertypreviews/components/AddressPreviewLink'

const FactBox = ({ ...props }) => <Box minHeight={50} mb={4} {...props} />

const FadeBox = styled(Box)`
  overflow: hidden;
  position: relative;

  &:after {
    position: absolute;
    top: 0px;
    right: 0px;
    bottom: 0px;
    width: 180px;
    content: ' ';
    background: linear-gradient(270deg, #FFFFFF 65.62%, rgba(255, 255, 255, 0) 100%);
  }
`

const CardCaption = ({ ...props }) => <Caption fontSize={0} display='block' color='secondary' {...props} />

const formatFinancial = value => typeof value === 'number' ? monetary(value, { decimals: 0 }) : '--'

const LastDeal = ({ contact }) => {
  const date = dateUtil.format(contact.latestLoanDate, dateUtil.formats.MONTH_YEAR)
  const amount = monetary(contact.latestLoanAmount, { decimals: 0 })

  let ret = ''
  if (date)
    ret += date

  if (amount) {
    if (date)
      ret += ': '

    ret += amount
  }

  return ret
}

export default function BuyerLenderCard ({ contact, ...props }) {
  const { minLent, maxLent, minInterest, maxInterest } = contact
  let spendRange = '--'
  if (minLent || maxLent)
    spendRange = `${formatFinancial(contact.minLent)} - ${formatFinancial(contact.maxLent)}`

  let intRange = ''
  if (minInterest || maxInterest)
    intRange = `${minInterest ? `${minInterest}%` : '--'} - ${maxInterest ? `${maxInterest}%` : '--'}`

  return (
    <Card borderTop='12px solid' borderColor='red.500' {...props}>
      <Box mb={5}>
        <Box style={{ position: 'relative' }}>
          <FadeBox>
            <Heading size={4} style={{ whiteSpace: 'nowrap' }}>{contact.name}</Heading>
          </FadeBox>
        </Box>
        <AddressPreviewLink
          fontSize={1}
          address={{
            line1: contact.addressLine1,
            city: contact.addressCity,
            state: contact.addressState,
            zip: contact.addressZip
          }}
        />
      </Box>
      <Box display='flex' alignItems='top' mx={-4}>
        <Box flex='2' mx={4}>
          <Box display='flex' mx={-4}>
            <Box flex='10' alignItems='top' mx={4}>
              <FactBox>
                <CardCaption>Avg Loan</CardCaption>
                <Heading size={4}>{formatFinancial(contact.avgLoanAmount)}</Heading>
                <Heading size={4}>
                  {contact.avgInterestRate && <span>{contact.avgInterestRate}% <Text color='secondary'>rate</Text></span>}
                </Heading>
              </FactBox>
              <FactBox>
                <CardCaption>Loan Range</CardCaption>
                <Box>
                  <Text>{spendRange}</Text>
                </Box>
                <Box>
                  {intRange && <Text>{intRange} <Text color='secondary'>rate</Text></Text>}
                </Box>
              </FactBox>
            </Box>

            <Box flex='13' alignItems='top' mx={4}>
              <FactBox>
                <CardCaption>Volume</CardCaption>
                <Heading size={5}>
                  {contact.totalLent
                    ? <span>
                      <Monetary useToK decimals={1}>{contact.totalLent}</Monetary>
                      {' '}<Text color='secondary' fontSize='inherit'>in</Text>
                      {' '}<Numeric decimals={0}>{contact.totalLoans}</Numeric>
                      {' '}<Text color='secondary' fontSize='inherit'>loans</Text>
                    </span>
                    : '--'}
                </Heading>
              </FactBox>
              <FactBox>
                <CardCaption>Last Loan</CardCaption>
                <Text><LastDeal contact={contact} /></Text>
              </FactBox>
            </Box>
          </Box>

        </Box>

        <Box flex='1' mx={4}>
          <CardCaption>Geography</CardCaption>
          <Paragraph mb={5}>
            <LocationTooltip locations={contact.states} label='State' pluralLabel='States' />
          </Paragraph>
          <Paragraph>
            <LocationTooltip locations={contact.counties} label='County' pluralLabel='Counties' />
          </Paragraph>
        </Box>

      </Box>
    </Card>
  )
}
